import { type ComponentType, type ReactNode, useState } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { Radio as AkRadio } from '@atlaskit/radio';
import { type RadioProps } from '@atlaskit/radio/types';
import { token } from '@atlaskit/tokens';

import { DEFAULT_TWC_PRICE, messages } from './messages';
import { type OptInEventContext } from './opt-in-analytics';
import {
	buildTwcFeatures,
	FeatureList,
	LegalText,
	LOOM_ONLY_FEATURES,
	LoomOnlyLogo,
	OptInHeading,
	OptInModalLayout,
	OptInSubtitle,
	TwcLogos,
	useOptInChoiceChanged,
	useOptInModalViewed,
	useOptInSelect,
} from './opt-in-shared';
import { type ChoiceValue } from './types';
import { type LoomLandOptInCohort } from './use-loom-land-opt-in';
import { type LoomEdition, type TwcPriceMode } from './use-twc-price';

/**
 * TWCG-2108 Loom Land opt-in — "Variant A" (TWC choice) design.
 *
 * Renders the "Loom works better in a collection" choice cards as an accordion:
 * the card whose radio is selected expands to show its feature list and its own
 * call-to-action, and the graphic panel cross-fades to that choice's
 * illustration. Matches the Figma "TWC _ Variant A" frames. The modal shell,
 * analytics, header, features and legal copy are shared with Variant B via
 * `opt-in-shared`.
 */

const CONTENT_TEST_ID = 'loom-land-opt-in-variant-a';

// Both illustrations are preloaded so switching cards never flashes.
const PANEL_ILLUSTRATIONS: ChoiceValue[] = ['teamwork-collection', 'loom-only'];

// Figma groups the heading and cards (20px apart) so only the legal text is
// pushed to the bottom of the column.
const mainCss = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.250'),
});

const cardsCss = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.150'),
	// Reserves the taller stack (expanded 4-feature TWC card ~281px + 12px gap +
	// collapsed Loom card 70px) so toggling cards doesn't shift the layout. A min
	// (not fixed) height lets longer translations or larger text grow the block
	// instead of clipping it.
	minHeight: '364px',
});

// Figma card: 8px radius, 20/24/20/16 padding, 20px gap, subtle border.
const cardCss = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.250'),
	paddingBlock: token('space.250'),
	paddingInlineStart: token('space.200'),
	paddingInlineEnd: token('space.300'),
	// Explicit fallbacks: these tokens aren't defined in every host theme, and an
	// unresolved border-width falls back to the 3px "medium" default.
	borderRadius: token('radius.large', '8px'),
	borderWidth: token('border.width', '1px'),
	borderStyle: 'solid',
	borderColor: token('color.border'),
});

const selectedCardCss = css({
	borderColor: token('color.border.selected'),
});

// Indents the expanded card body under the header text rather than the radio
// control (DS Radio width plus its label gap, ~24px), matching Figma.
const cardBodyCss = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.150'),
	paddingInlineStart: token('space.300'),
});

// Extra breathing room under the card subtitle so it reads as a header for the
// feature list: 12px body gap + 4px = 16px (space.200) before the bullets.
const cardSubtitleCss = css({
	marginBlockEnd: token('space.050'),
});

// The DS Radio's root `<label>` top-aligns the control (`align-items:
// flex-start`) for multi-line text labels, which leaves the circle sitting high
// beside our taller logo rows. It exposes no xcss/className hook, so we
// re-centre it via its root element (same approach as the JSM portal
// playground's DS overrides).
const cardRadioCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& > label': {
		alignItems: 'center',
	},
});

type CardRadioProps = {
	value: ChoiceValue;
	isChecked: boolean;
	onChange: (value: ChoiceValue) => void;
	ariaLabel: string;
	children: ReactNode;
};

// The DS `Radio` is a forwardRef component whose JSX signature, under this
// repo's @types/react, demands DOM-only attributes like `crossOrigin` as
// required props (TS2739). Casting it to a ComponentType keyed on the DS's own
// `RadioProps` restores the intended (all-optional) prop contract, mirroring the
// pattern used elsewhere in signup/.
const Radio = AkRadio as unknown as ComponentType<RadioProps>;

// The custom header (logos) is passed as the radio's `label`, keeping the whole
// row clickable and accessible without a hand-rolled input.
const CardRadio = ({ value, isChecked, onChange, ariaLabel, children }: CardRadioProps) => (
	<div css={cardRadioCss}>
		<Radio
			name="loom-land-choice"
			value={value}
			isChecked={isChecked}
			ariaLabel={ariaLabel}
			onChange={() => onChange(value)}
			label={children}
		/>
	</div>
);

export type LoomChoiceVariantAProps = {
	/**
	 * Optional callback invoked when the user confirms a choice via a card CTA,
	 * before the redirect to signup/provisioning (e.g. for analytics). The CTA
	 * itself always navigates to the choice's continue URL — pass a custom
	 * {@link onSelect} only to hook additional behaviour, not to override routing.
	 */
	onSelect?: (choice: ChoiceValue) => void;
	/**
	 * Which option is selected on first render. Defaults to Teamwork Collection
	 * (the recommended path). Primarily useful for previewing each expanded state
	 * in isolation (e.g. Storybook).
	 */
	initialChoice?: ChoiceValue;
	/**
	 * Monthly per-user upsell price shown in the Teamwork Collection pricing line.
	 * Passed as an ICU argument so the amount (or currency) can change without
	 * editing the translated copy. Defaults to {@link DEFAULT_TWC_PRICE}.
	 */
	price?: ReactNode;
	/** Price message to render for {@link price}; defaults to "more". */
	priceMode?: TwcPriceMode;
	/** Loom edition, used to pick the provisioned TWC edition; defaults to "premium". */
	edition?: LoomEdition;
	/** Original page query string, passed through to the Loom-only signup redirect. */
	search?: string;
	/** Experiment cell, attached to analytics for cohort joins. */
	cohort?: LoomLandOptInCohort;
	/**
	 * Override the Loom clip played over the graphic panel on load. Defaults to
	 * the production clip; primarily for previewing it locally (e.g. Storybook).
	 */
	previewVideoSrc?: string;
};

export const LoomChoiceVariantA = ({
	onSelect,
	initialChoice = 'teamwork-collection',
	price = DEFAULT_TWC_PRICE,
	priceMode = 'more',
	edition = 'premium',
	search = '',
	cohort,
	previewVideoSrc,
}: LoomChoiceVariantAProps) => {
	const intl = useIntl();
	const [choice, setChoice] = useState<ChoiceValue>(initialChoice);
	const analyticsContext: OptInEventContext = { variant: 'a', cohort, edition, priceMode };
	const handleSelect = useOptInSelect({ onSelect, context: analyticsContext, search });
	useOptInModalViewed(analyticsContext);
	const trackChoiceChanged = useOptInChoiceChanged(analyticsContext);

	const handleChoiceChange = (next: ChoiceValue) => {
		setChoice(next);
		trackChoiceChanged(next);
	};

	const isTwc = choice === 'teamwork-collection';

	return (
		<OptInModalLayout
			contentTestId={CONTENT_TEST_ID}
			contentLayout="choice"
			activeIllustration={choice}
			illustrations={PANEL_ILLUSTRATIONS}
			previewVideoSrc={previewVideoSrc}
		>
			<div css={mainCss}>
				<OptInHeading />

				<div css={cardsCss}>
					<div css={[cardCss, isTwc && selectedCardCss]}>
						<CardRadio
							value="teamwork-collection"
							isChecked={isTwc}
							onChange={handleChoiceChange}
							ariaLabel={intl.formatMessage(messages.twcRadioLabel)}
						>
							<TwcLogos />
						</CardRadio>

						{isTwc && (
							<div css={cardBodyCss}>
								<div css={cardSubtitleCss}>
									<OptInSubtitle message={messages.twcSubtitle} />
								</div>
								<FeatureList features={buildTwcFeatures(price, priceMode)} />
								<Button
									appearance="primary"
									shouldFitContainer
									onClick={() => handleSelect('teamwork-collection')}
								>
									<FormattedMessage {...messages.twcCta} />
								</Button>
							</div>
						)}
					</div>

					<div css={[cardCss, !isTwc && selectedCardCss]}>
						<CardRadio
							value="loom-only"
							isChecked={!isTwc}
							onChange={handleChoiceChange}
							ariaLabel={intl.formatMessage(messages.loomOnlyRadioLabel)}
						>
							<LoomOnlyLogo />
						</CardRadio>

						{!isTwc && (
							<div css={cardBodyCss}>
								<div css={cardSubtitleCss}>
									<OptInSubtitle message={messages.loomOnlySubtitle} />
								</div>
								<FeatureList features={LOOM_ONLY_FEATURES} />
								<Button shouldFitContainer onClick={() => handleSelect('loom-only')}>
									<FormattedMessage {...messages.loomOnlyCta} />
								</Button>
							</div>
						)}
					</div>
				</div>
			</div>

			<LegalText message={isTwc ? messages.legalTwc : messages.legalLoomOnly} />
		</OptInModalLayout>
	);
};
